/**
 * Der Filter vor dem Fehlerbericht — ADR-030, `OBS-001`.
 *
 * Ein Fehlerbericht führt von sich aus Request-Bodies, Formularinhalte,
 * Variablenwerte und Nutzerkennungen mit. Genau das schließt ADR-022 für die
 * zentrale Ebene aus, und ADR-030 verlangt die Durchsetzung **technisch**:
 * „ein vorgeschalteter Filter in `packages/telemetry` entfernt die verbotenen
 * Felder, bevor ein Ereignis den Stack verlässt."
 *
 * ## Die Felderliste allein genügt nicht
 *
 * ADR-030 zählt auf, was zulässig ist — Fehlertyp, Meldung, Stacktrace,
 * Versionen, Tenant- und Correlation-ID, Gerätekategorie, Programmpfad. Was
 * dort nicht steht: **Meldung und Stacktrace können selbst tragen, was
 * verboten ist.**
 *
 *     Cannot read file "Angebot_Mueller_2026.pdf"
 *     Invalid recipient: kunde@example.com
 *
 * Beides sind Fehlermeldungen, beide sind nach der Felderliste zulässig, und
 * beide enthalten genau das, was `05` §5.3 und ADR-022 draußen halten wollen.
 * Eine Positivliste über Feldnamen fängt das nicht — sie sieht nur, dass das
 * Feld `message` heißt.
 *
 * Deshalb zwei Riegel, wie bei `infra/stacks/portal_konfiguration.py` und aus
 * demselben Grund: Der erste entscheidet über Felder, der zweite über deren
 * Inhalt.
 *
 * ## Was dieser Filter **nicht** kann
 *
 * Er erkennt Muster: Mailadressen, Dateinamen mit bekannter Endung, IBANs,
 * Telefonnummern. **Freitext erkennt er nicht.** Diese Meldungen gehen durch:
 *
 *     Kunde Mueller nicht gefunden
 *     Re: Ihre Anfrage vom 12.08.
 *     Notiz konnte nicht gespeichert werden: Zaehlerstand abgelesen
 *
 * Ein Name, eine Betreffzeile, ein Notizinhalt — alles nach ADR-030 verboten,
 * und kein Muster der Welt unterscheidet sie zuverlässig von einer technischen
 * Meldung.
 *
 * **Daraus folgt eine Regel für den Code, nicht für den Filter:
 * Fehlermeldungen interpolieren keine Kundendaten.** Statt
 * `Kunde ${name} nicht gefunden` gehört dort `Kunde nicht gefunden` und die
 * Kennung in die Correlation-ID, über die sich der Fall im Kundenstack
 * nachschlagen lässt — dort, wo die Daten ohnehin liegen.
 *
 * Dieser Filter ist die zweite Verteidigungslinie und nicht die erste. Wer ihn
 * für die erste hält, baut Meldungen, die er ohne ihn nicht bauen würde.
 */

/**
 * Was ein Fehlerbericht tragen darf. Wörtlich aus ADR-030.
 *
 * Positivliste, nicht Ausschlussliste. Eine Ausschlussliste wäre am Tag nach
 * dem nächsten SDK-Update unvollständig, und niemand würde es merken: Was
 * durchrutscht, sieht in GlitchTip aus wie ein normaler Bericht.
 */
export const ERLAUBTE_FELDER = [
  "fehlertyp",
  "meldung",
  "stacktrace",
  "releaseVersion",
  "bundleVersion",
  "tenantId",
  "correlationId",
  "geraetekategorie",
  "browserversion",
  "programmpfad",
] as const;

export type ErlaubtesFeld = (typeof ERLAUBTE_FELDER)[number];

export type Fehlerbericht = Partial<Record<ErlaubtesFeld, string>>;

export interface Filterergebnis {
  readonly bericht: Fehlerbericht;
  /** Feldnamen, die entfernt wurden, weil sie nicht auf der Liste stehen. */
  readonly entfernt: readonly string[];
  /** Felder, in denen etwas maskiert wurde — mit Angabe, was. */
  readonly maskiert: readonly string[];
}

/**
 * Wonach im Inhalt gesucht wird.
 *
 * Jedes Muster steht hier mit dem Fall, den es abfängt. Die Reihenfolge ist
 * nicht beliebig: Eine Mailadresse enthält einen Punkt und würde sonst als
 * Dateiname erkannt und nur halb maskiert.
 */
const MUSTER: readonly { name: string; regex: RegExp; ersatz: string }[] = [
  // `kunde@example.com` in „Invalid recipient: …"
  { name: "mailadresse", regex: /[\w.+-]+@[\w-]+\.[\w.-]+/g, ersatz: "<mail>" },
  // `Angebot_Mueller_2026.pdf` in „Cannot read file …". Die Endungsliste ist
  // bewusst kurz und deckt, was in einer Handwerkerablage vorkommt; ein Muster
  // auf „irgendwas mit Punkt" träfe auch Versionsnummern und Modulpfade.
  {
    name: "dateiname",
    regex: /[\w äöüÄÖÜß.,()+-]+\.(pdf|docx?|xlsx?|odt|ods|jpe?g|png|heic|eml|msg|zip|xml)\b/gi,
    ersatz: "<datei>",
  },
  // IBAN — steht in E-Rechnungen und damit in deren Fehlermeldungen.
  { name: "iban", regex: /\b[A-Z]{2}\d{2}[A-Z0-9]{10,30}\b/g, ersatz: "<iban>" },
  // Telefonnummern in Kundendaten.
  { name: "telefon", regex: /\b(?:\+\d{1,3}[ /-]?)?\(?\d{3,5}\)?[ /-]?\d{3,}\b/g,
    ersatz: "<telefon>" },
];

/**
 * Pfade, die im Stacktrace stehen **dürfen**.
 *
 * Der Stacktrace ist der Grund, warum es Fehlerberichte gibt — ohne ihn bleibt
 * „geht nicht". Er enthält aber Dateipfade, und die Dateinamensregel oben
 * würde jeden davon maskieren. Unterschieden wird nach Herkunft: Unser eigener
 * Code darf erkennbar bleiben, eine Kundenablage nicht.
 */
const EIGENER_CODE = /(?:^|[\s(])((?:\/app|\/srv\/app|file:\/\/\/app|\.\/|src\/|packages\/|apps\/|services\/)[\w./@-]*)/g;

function inhaltPruefen(wert: string, feld: string,
                       maskiert: string[]): string {
  let aus = wert;
  // Eigene Codepfade zuerst herausnehmen und durch Platzhalter ersetzen, damit
  // die Dateinamensregel sie nicht erwischt. Danach zurücksetzen.
  const bewahrt: string[] = [];
  aus = aus.replace(EIGENER_CODE, (treffer, pfad: string) => {
    bewahrt.push(pfad);
    return treffer.replace(pfad, `\uE000${bewahrt.length - 1}\uE000`);
  });

  for (const m of MUSTER) {
    // `test` verbraucht bei globalen Mustern den Zustand — deshalb ein
    // frisches Muster je Prüfung. Sonst hängt das Ergebnis davon ab, was
    // vorher geprüft wurde, und das ist der schlechteste aller Fehler:
    // reproduzierbar nur manchmal.
    const regex = new RegExp(m.regex.source, m.regex.flags);
    if (regex.test(aus)) {
      maskiert.push(`${feld}: ${m.name}`);
      aus = aus.replace(new RegExp(m.regex.source, m.regex.flags), m.ersatz);
    }
  }

  // Der Platzhalter steht als **Escape**, nicht als rohes Zeichen: Die erste
  // Fassung setzte hier Nullbytes ein, und die sind doppelt falsch — ESLint
  // verbietet Steuerzeichen im Muster (`no-control-regex`), und ein rohes
  // Nullbyte in einer Quelldatei ueberlebt nicht jedes Werkzeug. Genau das
  // war am 22.08.2026 schon in `packages/einvoice-validation` zu beheben,
  // und hier ist es demselben Autor am selben Tag erneut passiert.
  //
  // `` liegt in der Private Use Area: kein Steuerzeichen, und in einer
  // Fehlermeldung praktisch nie zu erwarten. Ein Leerzeichen als Trenner
  // ginge nicht — Text enthaelt Leerzeichen.
  return aus.replace(/\uE000(\d+)\uE000/g, (_, i: string) => bewahrt[Number(i)] ?? "");
}

/**
 * Nimmt ein rohes Ereignis und gibt zurück, was den Stack verlassen darf.
 *
 * Wirft nicht. Ein Filter, der bei einem unerwarteten Ereignis abbricht,
 * verhindert den Bericht — und ein fehlender Fehlerbericht ist genau das
 * Problem, das GlitchTip lösen soll.
 */
export function filtern(roh: unknown): Filterergebnis {
  const entfernt: string[] = [];
  const maskiert: string[] = [];
  const bericht: Record<string, string> = {};

  if (typeof roh !== "object" || roh === null || Array.isArray(roh)) {
    return { bericht: {}, entfernt: [], maskiert: [] };
  }

  const erlaubt = new Set<string>(ERLAUBTE_FELDER);
  for (const [feld, wert] of Object.entries(roh as Record<string, unknown>)) {
    if (!erlaubt.has(feld)) {
      // Der erste Riegel. Was hier landet, ist meist harmlos benannt —
      // `extra`, `contexts`, `request` — und trägt trotzdem Formularinhalte.
      entfernt.push(feld);
      continue;
    }
    if (typeof wert !== "string") {
      // Auch ein erlaubtes Feld mit unerwartetem Typ fliegt raus. Ein Objekt
      // unter `meldung` wäre der bequeme Weg, einen ganzen Request-Body
      // mitzuschicken.
      entfernt.push(feld);
      continue;
    }
    bericht[feld] = inhaltPruefen(wert, feld, maskiert);
  }

  return { bericht: bericht as Fehlerbericht, entfernt, maskiert };
}
